import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { PayoutCompany } from '@/types'

const COLLECTION = 'payout_companies'

function fromFirestore(snap: QueryDocumentSnapshot<DocumentData>): PayoutCompany {
  const d = snap.data()
  return {
    id: snap.id,
    name: d.name || '',
    organizationNumber: d.organizationNumber,
    address: d.address,
    city: d.city,
    postalCode: d.postalCode,
    phoneNumber: d.phoneNumber,
    createdAt: d.createdAt?.toDate() ?? new Date(),
    updatedAt: d.updatedAt?.toDate() ?? new Date()
  }
}

export const payoutCompanyService = {
  async getPayoutCompanies(userId: string): Promise<PayoutCompany[]> {
    const q = query(
      collection(db, 'users', userId, COLLECTION),
      orderBy('name', 'asc')
    )
    const snap = await getDocs(q)
    return snap.docs.map(fromFirestore)
  },

  async getPayoutCompany(userId: string, companyId: string): Promise<PayoutCompany | null> {
    const ref = doc(db, 'users', userId, COLLECTION, companyId)
    const snap = await getDoc(ref)
    if (!snap.exists()) return null
    return fromFirestore(snap as QueryDocumentSnapshot<DocumentData>)
  },

  async createPayoutCompany(
    userId: string,
    data: Omit<PayoutCompany, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<PayoutCompany> {
    const ref = doc(collection(db, 'users', userId, COLLECTION))
    const now = serverTimestamp()
    await setDoc(ref, {
      name: data.name.trim(),
      organizationNumber: data.organizationNumber?.trim() || null,
      address: data.address?.trim() || null,
      city: data.city?.trim() || null,
      postalCode: data.postalCode?.trim() || null,
      phoneNumber: data.phoneNumber?.trim() || null,
      createdAt: now,
      updatedAt: now
    })
    const created = await getDoc(ref)
    return fromFirestore(created as QueryDocumentSnapshot<DocumentData>)
  },

  async updatePayoutCompany(
    userId: string,
    companyId: string,
    data: Partial<Omit<PayoutCompany, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<void> {
    const ref = doc(db, 'users', userId, COLLECTION, companyId)
    const updates: Record<string, any> = { updatedAt: serverTimestamp() }
    if (data.name !== undefined) updates.name = data.name.trim()
    if (data.organizationNumber !== undefined) updates.organizationNumber = data.organizationNumber?.trim() || null
    if (data.address !== undefined) updates.address = data.address?.trim() || null
    if (data.city !== undefined) updates.city = data.city?.trim() || null
    if (data.postalCode !== undefined) updates.postalCode = data.postalCode?.trim() || null
    if (data.phoneNumber !== undefined) updates.phoneNumber = data.phoneNumber?.trim() || null
    await updateDoc(ref, updates)
  },

  async deletePayoutCompany(userId: string, companyId: string): Promise<void> {
    const ref = doc(db, 'users', userId, COLLECTION, companyId)
    await deleteDoc(ref)
  }
}
